import React, { useCallback, useState } from 'react'
import { useDropzone } from 'react-dropzone'
import { ArrowExitUpIcon } from '../../../common/icons/general.tsx'
import {
  MAX_UPLOAD_FILE_SIZE,
  MAX_UPLOAD_FILE_SIZE_MB,
} from '@/common/constants.ts'
import { cn } from '@/lib/utils.ts'

interface Props {
  onFileAccepted: (file: File) => void
}

const ProviderFileUpload: React.FC<Props> = ({ onFileAccepted }) => {
  const [fileName, setFileName] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      const file = acceptedFiles[0]
      if (!file) return

      if (file.size > MAX_UPLOAD_FILE_SIZE) {
        setError(`File size exceeds the ${MAX_UPLOAD_FILE_SIZE_MB}MB limit.`)
        setFileName(null)
        return
      }

      setFileName(file.name)
      setError(null)
      onFileAccepted(file)
    },
    [onFileAccepted]
  )

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    multiple: false,
    accept: {
      'text/csv': ['.csv'],
      'application/vnd.ms-excel': ['.xls'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': [
        '.xlsx',
      ],
    },
    maxSize: MAX_UPLOAD_FILE_SIZE,
  })

  return (
    <div className="w-full">
      <div
        {...getRootProps()}
        className={cn(
          'p-10 rounded-md border-2 border-dashed text-center bg-white transition-colors duration-200',
          isDragActive ? 'border-blue-400 bg-blue-50' : 'border-gray-300'
        )}
      >
        <input {...getInputProps()} />

        <div className="flex justify-center mb-4">
          <ArrowExitUpIcon className="text-gray-400 text-5xl" />
        </div>

        <p className="text-gray-700 font-medium mb-1">
          Drop your CSV or Excel file here
        </p>
        <p className="text-primary underline cursor-pointer text-sm">
          or browse files
        </p>
      </div>

      {fileName && (
        <p className="mt-3 text-sm text-green-700 text-center font-medium">
          Selected: {fileName}
        </p>
      )}

      {error && (
        <p className="mt-3 text-sm text-red-600 text-center">{error}</p>
      )}
    </div>
  )
}

export default ProviderFileUpload
